'use client'

import { HostFormDialog } from '@/components/forms/host-form-dialog'
import dynamic from 'next/dynamic'
import {
  PremiumButton,
  PremiumEyebrow,
  PremiumFeature,
  PremiumHeading,
  PremiumHero,
  PremiumIntro,
  PremiumSection,
  PremiumStat,
} from '@/components/premium/premium-ui'
import { Button } from '@/components/ui/button'
import { Breadcrumbs } from '@/components/ui/breadcrumbs'
import { COIMBATORE_EXPERIENCES_URL } from '@/lib/links'
import { liveDistricts, liveDistrictsLabel, openingNextDistricts } from '@/lib/live-districts'
import {
  CalendarCheck,
  Camera,
  Landmark,
  ShieldCheck,
  Sparkles,
  Users,
  ArrowRight,
} from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

// MapLibre (~700KB) loads only when the map scrolls into view, never on page load.
const TamilNaduMap = dynamic(
  () => import('@/components/map/tamil-nadu-map').then((m) => m.TamilNaduMap),
  {
    ssr: false,
    loading: () => (
      <div className="grid h-full w-full place-items-center text-sm text-muted-foreground">
        Loading district map…
      </div>
    ),
  },
)

const destinations = [
  {
    title: 'Thanjavur',
    label: 'Live now',
    image: '/destinations/thanjavur-banner.webp',
    alt: 'Thanjavur banner — temples, crafts, local cuisine and village life, hosted by locals',
    body: 'Temples, crafts, traditions and timeless stories.',
    href: '/thanjavur',
    cta: 'View experiences',
  },
  {
    title: 'Kancheepuram',
    label: 'Opening next',
    image: '/destinations/kancheepuram-banner.webp',
    alt: 'Kancheepuram banner — temples, silk, heritage and artisans, hosted by locals',
    body: 'Silks, spirituality and sacred flavours.',
    href: '/experiences',
    cta: 'Preview experiences',
  },
  {
    title: 'Coimbatore',
    label: 'Live now',
    image: '/destinations/coimbatore-banner.webp',
    alt: 'Coimbatore banner — nature, crafts, local cuisine and hill experiences, hosted by locals',
    body: 'Nature, culture and local living.',
    href: COIMBATORE_EXPERIENCES_URL,
    external: true,
    cta: 'Check availability',
  },
  {
    title: 'Madurai',
    label: 'Opening next',
    image: '/destinations/madurai-banner.webp',
    alt: 'Madurai banner — temples, heritage, local cuisine and culture, hosted by locals',
    body: 'Heritage, flavours and vibrant local life.',
    href: '/experiences',
    cta: 'Preview experiences',
  },
]

const hostTypes = [
  ['Artisans', 'Craft masters and traditional skill practitioners.', '/experiences/athangudi-tiles-making.jpg'],
  ['Weavers', 'Handloom weavers and textile communities.', '/weekend-trips/silk-weaving-weaver.webp'],
  ['Homemakers', 'Food makers who love to cook and share traditions.', '/experiences/kuzhi-paniyaram-making-with-paati.jpg'],
  ['Artists & Performers', 'Musicians, dancers, storytellers and performers.', '/experiences/nadaswaram-lathe.jpg'],
  ['Farmers', 'Farmers and nature keepers with deep knowledge.', '/weekend-trips/betel-farm-walk.webp'],
  ['Village Protectors', 'Organisers of local festivals and community events.', '/experiences/pongalo-pongal-with-family.jpg'],
]

export function DestinationsPage() {
  return (
    <div className="bg-background">
      <PremiumHero
        image="/experiences/paddy-harvesting-in-wet-fields.jpg"
        imageAlt="Farmers harvesting paddy in Tamil Nadu wetlands — rural life experiences with Oorva"
        eyebrow="Handpicked Destinations"
        title={
          <>
            Where culture
            <br />
            comes alive
          </>
        }
        subtitle="Step into places where traditions thrive, stories are real, and experiences stay with you. No payment now — we confirm every date with you on WhatsApp."
      >
        <div className="grid max-w-3xl grid-cols-2 gap-6 sm:grid-cols-2">
          <PremiumStat value="Live now" label="Thanjavur & Coimbatore" />
          <PremiumStat value="Opening next" label="Kancheepuram & Madurai" />
        </div>
      </PremiumHero>

      <PremiumSection>
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Destinations' }]} />
        <div className="mb-6 mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <PremiumHeading>Explore Destinations</PremiumHeading>
            <PremiumIntro className="mt-3">Handpicked places. Unique experiences.</PremiumIntro>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <PremiumButton href="/thanjavur">View experiences in Thanjavur</PremiumButton>
          </div>
        </div>
        <div className="grid gap-6">
          {destinations.map((destination) => (
            <DestinationBannerCard key={destination.title} {...destination} />
          ))}
        </div>
        <p className="mt-4 text-xs leading-5 text-muted-foreground">
          Live districts open directly. Opening-next districts show a preview — leave your email on the host page and we notify you at launch.
        </p>
      </PremiumSection>

      <PremiumSection className="pt-0">
        <div className="grid gap-6 rounded-[1.5rem] border border-border bg-card p-4 md:p-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <PremiumEyebrow>District map</PremiumEyebrow>
            <PremiumHeading className="mt-2 text-3xl md:text-4xl">Live and opening next districts</PremiumHeading>
            <PremiumIntro className="mt-3">
              Start with {liveDistrictsLabel} today. We are curating hosts, routes and local partners across the next set of districts.
            </PremiumIntro>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <DistrictStatus title="Live now" districts={liveDistricts} tone="live" />
              <DistrictStatus title="Opening next" districts={openingNextDistricts} tone="soon" />
            </div>
          </div>
          <div className="h-[340px] overflow-hidden rounded-xl border border-border bg-secondary md:h-[440px]">
            <LazyDistrictMap />
          </div>
        </div>
      </PremiumSection>

      <PremiumSection tone="forest">
        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr_0.7fr] lg:items-center">
          <div>
            <PremiumHeading className="text-white">
              Every corner
              <br />
              <span className="italic text-brand-highlight">has a story.</span>
            </PremiumHeading>
            <p className="mt-4 text-white/68">
              We work with local communities to bring you authentic experiences that support
              heritage and create real impact.
            </p>
          </div>
          <div className="relative h-64 overflow-hidden rounded-xl border border-white/10">
            <Image
              src="/showcase-image-7.jpeg"
              alt="Oorva destinations"
              fill
              className="object-cover opacity-75"
              sizes="(min-width: 1024px) 33vw, 100vw"
            />
          </div>
          <div className="grid gap-4">
            <PremiumStat value="Verified" label="Local hosts" />
            <PremiumStat value="Community" label="Led experiences" />
            <PremiumStat value="Real" label="Sustainable impact" />
          </div>
        </div>
      </PremiumSection>
    </div>
  )
}

function DestinationBannerCard({
  image,
  alt,
  title,
  label,
  body,
  href,
  external,
  cta,
}: {
  image: string
  alt?: string
  title: string
  label?: string
  body?: string
  href: string
  external?: boolean
  cta?: string
}) {
  const isExternal = external ?? href.startsWith('http')

  return (
    <article className="group overflow-hidden rounded-[1.5rem] border border-border bg-card">
      <Link
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        aria-label={`${cta ?? 'Explore'} — ${title}`}
        className="block"
      >
        <div className="relative aspect-[16/10] w-full overflow-hidden bg-secondary sm:aspect-[21/9] lg:aspect-[1280/480]">
          <Image
            src={image}
            alt={alt ?? title}
            fill
            loading="lazy"
            quality={75}
            className="object-cover transition duration-500 group-hover:scale-[1.015]"
            sizes="(min-width: 1024px) 100vw, 100vw"
          />
          {label && (
            <span className="absolute left-4 top-4 inline-flex rounded-full border border-white/20 bg-primary/70 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white backdrop-blur-md">
              {label}
            </span>
          )}
        </div>
      </Link>
      <div className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <p className="text-sm leading-6 text-muted-foreground">{body}</p>
        <Link
          href={href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-foreground transition hover:text-brand"
        >
          {cta} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </article>
  )
}

function DistrictStatus({
  title,
  districts,
  tone,
}: {
  title: string
  districts: string[]
  tone: 'live' | 'soon'
}) {
  return (
    <div className="rounded-xl border border-border bg-background p-4">
      <div className="flex items-center gap-2">
        <span className={tone === 'live' ? 'h-2.5 w-2.5 rounded-full bg-primary' : 'h-2.5 w-2.5 rounded-full border border-brand bg-transparent'} />
        <h3 className="text-sm font-bold text-foreground">{title}</h3>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {districts.map((district) => (
          <Link
            key={district}
            href={tone === 'live' ? '/experiences' : '/host'}
            className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground transition hover:border-brand/40 hover:text-foreground"
          >
            {district}
          </Link>
        ))}
      </div>
    </div>
  )
}

function LazyDistrictMap() {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el || visible) return
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '240px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [visible])

  return (
    <div ref={ref} className="h-full w-full">
      {visible ? (
        <TamilNaduMap liveDistricts={liveDistricts} comingSoonDistricts={openingNextDistricts} />
      ) : (
        <div className="grid h-full w-full place-items-center text-sm text-muted-foreground">
          Loading district map…
        </div>
      )}
    </div>
  )
}

export function HostPage() {
  const handleSuccess = () => {
    toast.success('Thanks for applying', { description: 'Our team will get in touch soon.' })
  }
  const handleError = () => {
    toast.error('Submission failed', { description: 'Please try again or contact support.' })
  }

  return (
    <div className="bg-background">
      <PremiumHero
        image="/weekend-trips/doll-making-painting.png"
        imageAlt="Traveller painting a Thanjavur dancing doll with a local artisan host"
        eyebrow="Become an Oorva Host"
        title={
          <>
            Share your world.
            <br />
            <span className="italic text-accent-foreground">Inspire theirs.</span>
          </>
        }
        subtitle="Open your home, your skills and your stories to curious travellers who value real cultural experiences."
      >
        <HostFormDialog
          trigger={<Button className="min-h-12 rounded-full bg-brand px-7 text-white hover:bg-brand-hover">Join as a host</Button>}
          appearance="homepage"
          onSuccess={handleSuccess}
          onError={handleError}
        />
      </PremiumHero>

      <PremiumSection>
        <div className="mx-auto max-w-3xl text-center">
          <PremiumHeading className="text-3xl md:text-5xl">Who can be an Oorva Host?</PremiumHeading>
          <PremiumIntro className="mt-4">Anyone who carries a tradition, a story or a cultural way of life.</PremiumIntro>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {hostTypes.map(([title, body, image]) => (
            <div key={title} className="overflow-hidden rounded-[1.5rem] border border-border bg-card">
              <div className="relative h-44">
                <Image
                  src={image}
                  alt={title}
                  fill
                  className="object-cover"
                  sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                />
              </div>
              <div className="p-4">
                <h3 className="font-semibold">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </PremiumSection>

      <PremiumSection className="pt-0">
        <div>
          <PremiumHeading className="text-center text-3xl md:text-5xl">Benefits of being an Oorva Host</PremiumHeading>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            <PremiumFeature icon={<Sparkles className="h-5 w-5" />} title="Empowerment" body="Become a cultural ambassador for your community." />
            <PremiumFeature icon={<CalendarCheck className="h-5 w-5" />} title="Flexible" body="Host on your time, with no interference." />
            <PremiumFeature icon={<Landmark className="h-5 w-5" />} title="Income" body="Earn fairly for your skills and tradition." />
            <PremiumFeature icon={<ShieldCheck className="h-5 w-5" />} title="Protect what matters" body="Keep traditions alive with respect." />
            <PremiumFeature icon={<Users className="h-5 w-5" />} title="Small groups" body="No overcrowding, only respectful guests." />
            <PremiumFeature icon={<Camera className="h-5 w-5" />} title="Visibility" body="Gain recognition for your craft and story." />
          </div>
        </div>
      </PremiumSection>

      <PremiumSection className="pt-0">
        <div className="rounded-[1.5rem] border border-border bg-card p-6 md:p-10">
          <PremiumHeading className="text-3xl md:text-4xl">Before you apply</PremiumHeading>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            <p className="text-sm leading-7 text-muted-foreground"><strong className="text-foreground">Where we are active.</strong> Oorva currently runs experiences in {liveDistrictsLabel}. Tell us your district even if it is opening next; our team will explain whether we can onboard there.</p>
            <p className="text-sm leading-7 text-muted-foreground"><strong className="text-foreground">Earnings and schedule.</strong> Your activity, group size, hosting dates and payment terms are agreed with you during onboarding. Applying does not commit you to host.</p>
            <p className="text-sm leading-7 text-muted-foreground"><strong className="text-foreground">What happens next.</strong> Share your contact details, district and category. Our team will contact you to learn about your experience, discuss visitor readiness and explain the next steps.</p>
            <p className="text-sm leading-7 text-muted-foreground"><strong className="text-foreground">Questions first?</strong> Email <a className="underline" href="mailto:hello@oorva.in">hello@oorva.in</a>. We will confirm the response timeline when we receive your enquiry.</p>
          </div>
        </div>
      </PremiumSection>

      <PremiumSection tone="forest">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <PremiumHeading className="text-white">How to become a host</PremiumHeading>
            <div className="mt-8 grid gap-4 sm:grid-cols-4">
              {['Apply', 'Our team connects', 'Onboarding', 'Start hosting'].map((step, index) => (
                <div key={step} className="rounded-2xl border border-white/10 bg-white/[0.055] p-5">
                  <div className="text-xs font-bold text-brand-highlight">0{index + 1}</div>
                  <h3 className="mt-3 font-semibold">{step}</h3>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/[0.055] p-8">
            <h3 className="font-sans text-4xl font-semibold tracking-[-0.035em] text-white">Ready to start your hosting journey?</h3>
            <p className="mt-4 text-white/62">Tell us about your cultural experience and we will guide you from there.</p>
            <div className="mt-7">
              <HostFormDialog
                trigger={<Button className="min-h-12 rounded-full bg-brand px-7 text-white hover:bg-brand-hover">Submit application</Button>}
                appearance="homepage"
                onSuccess={handleSuccess}
                onError={handleError}
              />
            </div>
          </div>
        </div>
      </PremiumSection>
    </div>
  )
}
